import React from 'react'
import { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Avatar,
  Badge,
  BlockStack,
  Box,
  Button,
  Icon,
  IndexTable,
  InlineGrid,
  InlineStack,
  Popover,
  SkeletonBodyText,
  SkeletonDisplayText,
  Text,
  Tooltip,
} from '@shopify/polaris'
import {
  ChevronDownIcon,
  ChevronUpIcon,
  ClipboardCheckFilledIcon,
  ClipboardIcon,
  DeleteIcon,
  EditIcon,
  MenuVerticalIcon,
  PlusIcon,
  StatusIcon,
} from '@shopify/polaris-icons'
import CreateSection from './CreateSection.jsx'
import EditExpModal from './EditExpModal.jsx'
import { useRedirect } from '../../../hooks/useRedirect.js'
import { ExperienceStatuses } from '../../../constants/enums.js'
import queryClient from '../../../utils/query.js'
import { deleteExperience, updateExperience } from '../../../apis/experience.js'
import { percentageFormatter } from '../../../utils/formater.js'
import analytics, { MixPanelAnalytics } from '../../../utils/analytics.js'

export default function TableRow({
  experience,
  index,
  filters,
  anyFiltersApplied,
  sectionStatsArray,
  statsLoading,
  activeModal,
}) {
  const {
    name: experienceName,
    status: experienceStatus,
    sections: experienceSections,
    id: experienceId,
    visitorPercentage,
    isDefault,
  } = experience
  const [popoverActive, setPopoverActive] = useState(false)
  const togglePopoverActive = useCallback(
    () => setPopoverActive((popoverActive) => !popoverActive),
    [],
  )
  const [collapsed, setCollapsed] = useState(false)
  const [isEditting, setEditting] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  const { redirectToLink } = useRedirect()
  const sections = experienceSections.filter(
    ({ title, type, page, enabled }) => {
      let showSection = true
      if (filters.query) {
        let show = false
        let titleString = title.toLowerCase()
        showSection = titleString.includes(filters.query.toLowerCase())
      }
      if (
        filters.recommendation?.length &&
        !filters.recommendation?.find((val) => val === type)
      ) {
        showSection = false
      }
      if (filters.page?.length && !filters.page?.find((val) => val === page)) {
        showSection = false
      }
      if (filters.status?.length) {
        filters.status = filters.status.map((val) => String(val))
        if (!filters.status.find((val) => val === String(enabled))) {
          showSection = false
        }
      }
      if (filters.showEnabledSections !== null) {
        if (enabled !== filters.showEnabledSections) {
          showSection = false
        }
      }
      return showSection
    },
  )

  const statusChangeHandler = async (status) => {
    if (isDefault) {
      shopify.toast.show(t('Section.List.defaultExpStatusChange'), {
        isError: true,
      })
      return
    }
    setLoading(true)
    const { data, error } = await updateExperience(experienceId, { status })
    if (error) {
      console.log('Error updating experience status', error)
      return
    }
    analytics.trackExperienceEvent(
      MixPanelAnalytics.Actions.UPDATE,
      data.experience,
    )
    await queryClient.invalidateQueries(['shopDetails'])
    shopify.toast.show(t('Section.List.expStatusSuccess'))
    setLoading(false)
  }

  const head = (
    <IndexTable.Row disabled>
      <IndexTable.Cell colSpan={6} scope="colgroup" as="th">
        <div style={{ padding: '5px 0' }}>
          <InlineStack align="space-between">
            <InlineGrid
              gap={'400'}
              columns={'auto auto auto'}
              alignItems="center"
            >
              <InlineGrid columns={'auto 1fr'} gap={'200'}>
                <Button
                  variant="monochromePlain"
                  icon={collapsed ? ChevronDownIcon : ChevronUpIcon}
                  onClick={() => {
                    setCollapsed(!collapsed)
                  }}
                />
                <Text as="p" fontWeight="semibold">
                  {experienceName}
                </Text>
              </InlineGrid>
              {isDefault && (
                <Badge size="small">{t('Section.List.expDefault')}</Badge>
              )}
              <Badge
                size="small"
                tone={
                  experienceStatus === ExperienceStatuses.PUBLISHED
                    ? 'success'
                    : 'critical'
                }
              >
                {t(`Section.List.Filter.expStatuses.${experienceStatus}`)}
              </Badge>
            </InlineGrid>
            <Text as="p" tone="subdued" fontWeight="medium">
              {visitorPercentage}% {t('Section.List.Table.subheader.visitors')}
            </Text>
            <Popover
              active={popoverActive}
              activator={
                <Button
                  onClick={togglePopoverActive}
                  icon={MenuVerticalIcon}
                  variant="plain"
                  size="slim"
                />
              }
              onClose={togglePopoverActive}
            >
              <Popover.Pane>
                <div
                  style={{
                    width: 200,
                    padding: 'var(--p-space-200)',
                  }}
                >
                  <BlockStack gap={200}>
                    <Button
                      textAlign="start"
                      icon={StatusIcon}
                      loading={loading}
                      onClick={async () => {
                        await statusChangeHandler(
                          experienceStatus === ExperienceStatuses.PUBLISHED
                            ? ExperienceStatuses.UNPUBLISHED
                            : ExperienceStatuses.PUBLISHED,
                        )
                        togglePopoverActive()
                      }}
                      variant="tertiary"
                    >
                      {t(
                        `Section.List.experienceStatusCTA.${experienceStatus}`,
                      )}
                    </Button>
                    <Button
                      textAlign="start"
                      variant="tertiary"
                      icon={PlusIcon}
                      onClick={() => {
                        redirectToLink({
                          url: '/sections/create?experienceId=' + experienceId,
                        })
                        setPopoverActive(false)
                      }}
                    >
                      {t('Section.List.Table.emptyState.cta')}
                    </Button>
                    <Button
                      variant="tertiary"
                      textAlign="start"
                      icon={EditIcon}
                      onClick={() => {
                        setEditting(true)
                        togglePopoverActive()
                      }}
                    >
                      {t('Section.List.editExpCta')}
                    </Button>
                    <Button
                      variant="tertiary"
                      textAlign="start"
                      loading={deleting}
                      icon={DeleteIcon}
                      onClick={async () => {
                        if (isDefault) {
                          shopify.toast.show(
                            t('Section.List.defaultExpDelete'),
                            {
                              isError: true,
                            },
                          )
                          return
                        }
                        if (!confirm(t('Section.List.deleteExpConfirm'))) {
                          return
                        }
                        setDeleting(true)
                        const res = await deleteExperience(experienceId)
                        analytics.trackExperienceEvent(
                          MixPanelAnalytics.Actions.DELETE,
                          experience,
                        )
                        setDeleting(false)
                        if (
                          res.error?.error ===
                          'experience.errors.publishedCanNotBeDeleted'
                        ) {
                          shopify.toast.show(
                            t('Section.List.publishedCanNotBeDeleted'),
                            {
                              isError: true,
                            },
                          )
                          return
                        }
                        await queryClient.invalidateQueries(['shopDetails'])
                        shopify.toast.show(t('Section.List.deleteExpSuccess'))
                        togglePopoverActive()
                      }}
                    >
                      {t('ABExperience.deleteCta')}
                    </Button>
                  </BlockStack>
                </div>
              </Popover.Pane>
            </Popover>
          </InlineStack>
        </div>
      </IndexTable.Cell>
    </IndexTable.Row>
  )

  if (sections.length === 0) {
    return (
      <>
        {head}
        <IndexTable.Row disabled>
          <IndexTable.Cell colSpan={6} scope="colgroup" as="th">
            <Box paddingBlock={'400'}>
              {!anyFiltersApplied && (
                <CreateSection
                  experienceId={experience.id}
                  activeModal={activeModal}
                />
              )}
              {anyFiltersApplied && (
                <InlineStack align="center">
                  <Text as="strong" tone="subdued">
                    {t('Section.NotFound.heading')}
                  </Text>
                </InlineStack>
              )}
            </Box>
          </IndexTable.Cell>
        </IndexTable.Row>
        <EditExpModal
          open={isEditting}
          setOpen={setEditting}
          experience={experience}
        />
      </>
    )
  }

  return (
    <>
      {head}
      {!collapsed && (
        <>
          <IndexTable.Row tone="subdued">
            <IndexTable.Cell as="th">
              {t('Section.List.Table.headers.section')}
            </IndexTable.Cell>
            <IndexTable.Cell as="th">
              {t('Section.List.Table.headers.type')}
            </IndexTable.Cell>
            <IndexTable.Cell as="th">
              {t('Section.List.Table.headers.page')}
            </IndexTable.Cell>
            <IndexTable.Cell as="th">
              {t('Section.List.Table.headers.stats')}
            </IndexTable.Cell>
            <IndexTable.Cell as="th">
              {t('Section.List.Table.headers.status')}
            </IndexTable.Cell>
          </IndexTable.Row>
          {sections.map((section) => (
            <SectionOption section={section} />
          ))}
        </>
      )}
      <EditExpModal
        open={isEditting}
        setOpen={setEditting}
        experience={experience}
      />
    </>
  )

  function SectionOption({ section }) {
    const { id, title, type, page, enabled } = section
    const [copied, setCopied] = useState(false)
    return (
      <IndexTable.Row
        id={id}
        key={id}
        position={index}
        onClick={() =>
          redirectToLink({
            url: '/sections/' + id,
            external: false,
          })
        }
      >
        <IndexTable.Cell>
          <div
            style={{
              display: 'flex',
              gap: '10px',
              alignItems: 'center',
              margin: '5px 0',
            }}
          >
            <div style={{ minWidth: '2rem' }}>
              <Avatar
                customer
                size="sm"
                // name={name}
              />
            </div>
            <div>
              <Text as="h3" variant="bodyMd" fontWeight="bold">
                {title}
              </Text>
              <InlineStack align="left" gap="100">
                <Text tone="base">ID: {id}</Text>
                <Tooltip
                  content={
                    <span>
                      {copied
                        ? t(
                            'Section.Details.DesignDetails.CustomAlignment.doneCta',
                          )
                        : t(
                            'Section.Details.DesignDetails.CustomAlignment.cta',
                          )}
                    </span>
                  }
                  preferredPosition="above"
                >
                  <span
                    onClick={(e) => {
                      e.stopPropagation()
                      navigator.clipboard.writeText(id)
                      setCopied(true)
                      setTimeout(() => setCopied(false), 1000)
                    }}
                  >
                    <Icon
                      source={copied ? ClipboardCheckFilledIcon : ClipboardIcon}
                      tone="base"
                    />
                  </span>
                </Tooltip>
              </InlineStack>
            </div>
          </div>
        </IndexTable.Cell>
        <IndexTable.Cell>{t(`Widgets.${type}.label`)}</IndexTable.Cell>
        <IndexTable.Cell>{t(`Pages.${page}.label`)}</IndexTable.Cell>
        <IndexTable.Cell>
          <InlineStack gap="300">
            <InlineStack gap="100">
              <Text>CTR:</Text>
              <Text fontWeight="semibold">
                {statsLoading ? (
                  <span
                    style={{
                      display: 'block',
                      width: '50px',
                    }}
                  >
                    <SkeletonDisplayText maxWidth="50px" size="small" />
                  </span>
                ) : (
                  percentageFormatter(
                    sectionStatsArray.find((stat) => stat.section == id)
                      ?.interactionRate ?? 0,
                  )
                )}
              </Text>
            </InlineStack>
          </InlineStack>
        </IndexTable.Cell>
        <IndexTable.Cell>
          <Badge progress={'complete'} tone={enabled ? 'success' : 'critical'}>
            {enabled
              ? t('Section.List.enums.sectionStatus.enabled')
              : t(`Section.List.enums.sectionStatus.disabled`)}
          </Badge>
        </IndexTable.Cell>
      </IndexTable.Row>
    )
  }
}

export function SkeletonRow() {
  return (
    <IndexTable.Row>
      <IndexTable.Cell>
        <Box padding="200">
          <SkeletonBodyText maxWidth="100%" size="small" lines={1} />
        </Box>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Box padding="200">
          <SkeletonBodyText maxWidth="100%" size="small" lines={1} />
        </Box>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Box padding="200">
          <SkeletonBodyText maxWidth="100%" size="small" lines={1} />
        </Box>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Box padding="200">
          <SkeletonBodyText maxWidth="100%" size="small" lines={1} />
        </Box>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Box padding="200">
          <SkeletonBodyText maxWidth="100%" size="small" lines={1} />
        </Box>
      </IndexTable.Cell>
    </IndexTable.Row>
  )
}
